import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Home as HomeIcon, KeyRound, Landmark, Scale, MessageSquare, FileText } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import Marquee from "@/components/Marquee";
import PropertyCard from "@/components/PropertyCard";
import { scrollToId } from "@/lib/scrollTo";
import { HOME, MANIFESTO, SERVICOS, CIDADES_ATENDIDAS, CONTATO } from "@/content/site";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=2000&q=85";

const TIPOS = ["Casa", "Apartamento", "Sobrado", "Terreno", "Casa de Praia"];
const SERVICO_ICONS = [HomeIcon, KeyRound, Landmark, Scale, MessageSquare, FileText];

const selectCls =
  "h-12 flex-1 rounded-none border-white/10 bg-white/5 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-300 focus:ring-0";
const contentCls = "rounded-none border-white/10 bg-elevated text-stone-200";

const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const [negocio, setNegocio] = useState("todos");
  const [tipo, setTipo] = useState("todos");
  const [cidade, setCidade] = useState("todas");
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const buscar = () => {
    const params = new URLSearchParams();
    if (negocio !== "todos") params.set("negocio", negocio);
    if (tipo !== "todos") params.set("tipo", tipo);
    if (cidade !== "todas") params.set("cidade", cidade);
    navigate(`/imoveis${params.toString() ? `?${params}` : ""}`);
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Casa com jardim ao entardecer" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-obsidian/30" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-40 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-stone-200"
          data-testid="hero-overline"
        >
          <span className="h-px w-8 bg-brand" /> {HOME.overline}
        </motion.p>

        <MaskedLines
          className="mt-6 font-serif text-4xl leading-[1.06] tracking-tight text-[#F4F4F6] sm:text-5xl lg:text-6xl"
          delay={0.35}
          lines={[
            <>{HOME.tituloLinha1}</>,
            <>
              <em className="italic text-brand">{HOME.tituloLinha2Destaque}</em>
              {HOME.tituloLinha2Resto}
            </>,
          ]}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-6 max-w-xl text-base font-light leading-relaxed text-stone-300"
        >
          {HOME.subtitulo}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          data-testid="hero-quick-search"
          className="mt-12 flex flex-col gap-3 border border-white/10 bg-obsidian/70 p-4 backdrop-blur-xl lg:flex-row lg:items-center lg:gap-4 lg:p-5"
        >
          <Select value={negocio} onValueChange={setNegocio}>
            <SelectTrigger data-testid="hero-filter-negocio" className={selectCls}>
              <SelectValue placeholder="Comprar ou alugar" />
            </SelectTrigger>
            <SelectContent className={contentCls}>
              <SelectItem value="todos">Comprar ou alugar</SelectItem>
              <SelectItem value="Venda">Comprar</SelectItem>
              <SelectItem value="Aluguel">Alugar</SelectItem>
            </SelectContent>
          </Select>

          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger data-testid="hero-filter-tipo" className={selectCls}>
              <SelectValue placeholder="Tipo de imóvel" />
            </SelectTrigger>
            <SelectContent className={contentCls}>
              <SelectItem value="todos">Todos os tipos</SelectItem>
              {TIPOS.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={cidade} onValueChange={setCidade}>
            <SelectTrigger data-testid="hero-filter-cidade" className={selectCls}>
              <SelectValue placeholder="Cidade" />
            </SelectTrigger>
            <SelectContent className={contentCls}>
              <SelectItem value="todas">Todas as cidades</SelectItem>
              {CIDADES_ATENDIDAS.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            onClick={buscar}
            data-testid="hero-search-button"
            className="group flex h-12 items-center justify-center gap-3 bg-brand px-8 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-light"
          >
            Buscar
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("destaques")}
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 z-10 hidden text-stone-400 transition-colors duration-300 hover:text-brand lg:block"
        aria-label="Rolar para destaques"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.button>
    </section>
  );
};

const Home = () => {
  const [destaques, setDestaques] = useState([]);

  useEffect(() => {
    axios.get(`${API}/properties`, { params: { destaque: true } }).then((res) => setDestaques(res.data));
  }, []);

  return (
    <main>
      <Hero />

      <section id="destaques" data-testid="featured-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <SectionHeading overline={HOME.destaquesOverline} lines={HOME.destaquesTitulo.map((l) => <>{l}</>)} />
          <Reveal delay={0.15}>
            <Link
              to="/imoveis"
              data-testid="featured-view-all-link"
              className="group inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-300 transition-colors duration-300 hover:text-brand"
            >
              {HOME.destaquesLink}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12">
          {destaques.slice(0, 4).map((p, i) => (
            <PropertyCard
              key={p.slug}
              property={p}
              index={i}
              className={i === 0 ? "md:col-span-7 min-h-[520px]" : i === 3 ? "md:col-span-7" : "md:col-span-5"}
            />
          ))}
        </div>
      </section>

      <Marquee />

      <section id="servicos" data-testid="services-section" className="border-b border-white/10 bg-coal">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <SectionHeading overline={HOME.servicosOverline} lines={HOME.servicosTitulo.map((l) => <>{l}</>)} />
          <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICOS.map((s, i) => {
              const Icon = SERVICO_ICONS[i % SERVICO_ICONS.length];
              return (
                <Reveal key={s.titulo} delay={i * 0.07} className="bg-coal">
                  <div data-testid={`service-${i}`} className="group h-full p-8 transition-colors duration-500 hover:bg-obsidian lg:p-10">
                    <span className="flex h-11 w-11 items-center justify-center border border-brand/40 text-brand transition-colors duration-500 group-hover:bg-brand group-hover:text-white">
                      <Icon size={18} />
                    </span>
                    <h3 className="mt-6 font-serif text-xl text-[#F4F4F6]">{s.titulo}</h3>
                    <p className="mt-3 text-sm font-light leading-relaxed text-stone-400">{s.texto}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section id="manifesto" data-testid="manifesto-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <SectionHeading overline={MANIFESTO.overline} lines={MANIFESTO.titulo.map((l) => <>{l}</>)} className="max-w-3xl" />
        <div className="mt-16 grid gap-px bg-white/10 md:grid-cols-2">
          {MANIFESTO.itens.map((cap, i) => (
            <Reveal key={cap.num} delay={i * 0.08} className="bg-obsidian">
              <div data-testid={`manifesto-chapter-${cap.num}`} className="group h-full p-10 transition-colors duration-500 hover:bg-coal lg:p-14">
                <span className="font-mono text-sm tracking-[0.3em] text-sun">{cap.num}</span>
                <h3 className="mt-6 font-serif text-xl text-[#F4F4F6] sm:text-2xl">{cap.titulo}</h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-stone-400 sm:text-base">{cap.texto}</p>
                <div className="mt-8 h-px w-12 bg-brand/50 transition-[width] duration-700 group-hover:w-24" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="cta-section" className="border-t border-white/10 bg-brand">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 py-20 lg:flex-row lg:items-center lg:justify-between lg:px-10 lg:py-24">
          <div>
            <Reveal>
              <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-white/70">{HOME.ctaOverline}</p>
            </Reveal>
            <MaskedLines
              inView
              className="mt-4 font-serif text-2xl leading-snug tracking-tight text-white sm:text-3xl lg:text-4xl"
              lines={HOME.ctaTitulo.map((l) => <>{l}</>)}
            />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-white/80">{HOME.ctaTexto}</p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/contato"
                data-testid="cta-contato-link"
                className="group inline-flex items-center justify-center gap-3 bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-brand-dark transition-colors duration-300 hover:bg-sun-light"
              >
                Enviar mensagem
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={`https://wa.me/${CONTATO.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-link"
                className="inline-flex items-center justify-center gap-3 border border-white/60 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white/10"
              >
                WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
